/** F036 — pure helpers for the KB profile enrichment surface.
 *
 * All functions are stateless/pure (no React, no side-effects) so they can be
 * unit-tested without a DOM or component mount.
 */

import type { KbProfile } from '@/services/kbProfileService';

// ── Card identity ─────────────────────────────────────────────────────────────

export type EnrichmentCardId =
  | 'differentiators'
  | 'voice'
  | 'faq'
  | 'restrictions'
  | 'limits'
  | 'slots'
  | 'links'
  | 'policies'
  | 'proactive_notice';

/** Canonical set — order is display-fallback, not priority. */
export const ENRICHMENT_CARDS: EnrichmentCardId[] = [
  'differentiators',
  'voice',
  'faq',
  'restrictions',
  'limits',
  'slots',
  'links',
  'policies',
  'proactive_notice',
];

/**
 * Highest → lowest impact order used when deriving recommendations and for
 * filling the "unfilled" bucket in `orderCards`.  Rationale: FAQ dramatically
 * reduces escalation; restrictions (F167) deflect more wrong leads than links
 * do — right after FAQ; links surface conversion moments early; voice makes
 * the bot feel owned; limits prevent liability; differentiators improve
 * persuasion; slots improve data capture; policies handle edge-cases.
 */
export const IMPACT_ORDER: EnrichmentCardId[] = [
  'faq',
  'restrictions',
  'links',
  'voice',
  'limits',
  'differentiators',
  'slots',
  'policies',
  'proactive_notice',
];

// ── Per-card fill predicates ──────────────────────────────────────────────────

/** Returns true when the card's field(s) are considered filled/populated. */
export function cardFilled(profile: KbProfile, id: EnrichmentCardId): boolean {
  switch (id) {
    case 'differentiators':
      return (
        (!!profile.differentiators && profile.differentiators.trim().length > 0) ||
        (!!profile.team_credentials && profile.team_credentials.trim().length > 0)
      );

    case 'voice':
      return (
        (!!profile.tone && profile.tone.trim().length > 0) ||
        (!!profile.voice_example && profile.voice_example.trim().length > 0)
      );

    case 'faq':
      return Array.isArray(profile.faq) && profile.faq.length > 0;

    case 'restrictions':
      return Array.isArray(profile.restrictions) && profile.restrictions.length > 0;

    case 'limits':
      return (
        (Array.isArray(profile.forbidden_scope) && profile.forbidden_scope.length > 0) ||
        (!!profile.persistent_disclaimer && profile.persistent_disclaimer.trim().length > 0) ||
        (!!profile.never_say && profile.never_say.trim().length > 0) ||
        (!!profile.refer_external_destination &&
          profile.refer_external_destination.trim().length > 0)
      );

    case 'slots':
      return Array.isArray(profile.slot_definitions) && profile.slot_definitions.length > 0;

    case 'links':
      return Array.isArray(profile.link_library) && profile.link_library.length > 0;

    case 'policies':
      return !!profile.policies && profile.policies.trim().length > 0;

    case 'proactive_notice':
      return !!profile.proactive_notice && profile.proactive_notice.trim().length > 0;

    default:
      // TypeScript exhaustiveness guard — this branch is unreachable at runtime.
      return ((_: never) => false)(id as never);
  }
}

// ── Completeness meter (F174 FR2) ───────────────────────────────────────────

export interface CompletenessResult {
  filled: number;
  total: number;
  pct: number;
}

/** F174 FR2 — 50/50 default weighting (founder-decided at refine, 2026-08-19):
 * the wizard minimum is worth half the meter, the enrichment cards the other
 * half. Before this, `pct` was cards-only, so a freshly-activated tenant who
 * had just finished the whole wizard read "Bot 0% configurado" — the meter
 * ignored everything it took to go live. */
const WIZARD_WEIGHT_PCT = 50;
const CARDS_WEIGHT_PCT = 50;

/** True when every field the KB wizard step actually asks for is non-empty:
 * offers, modalities, essence (`one_liner`), catalog (≥1 item), operation
 * (at least one modality-conditional field or a modality-independent
 * payment method), and objective (`bot_goal`). Note this is a stricter bar
 * than the wizard's own submit-time validation (which only hard-requires
 * offers/modalities/bot_goal/handoff_contact) — it's a deliberate meter-only
 * floor that rewards actually filling in the substantive wizard fields. */
function wizardMinimumComplete(profile: KbProfile): boolean {
  return (
    profile.offers.length > 0 &&
    profile.modalities.length > 0 &&
    !!profile.one_liner &&
    profile.one_liner.trim().length > 0 &&
    profile.catalog.length >= 1 &&
    operationHasData(profile.operation) &&
    profile.bot_goal !== null
  );
}

function operationHasData(operation: KbProfile['operation']): boolean {
  return Object.values(operation ?? {}).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  });
}

/** Raw (unrounded, uncapped-below-0) percentage for a given filled-card
 * count — factored out so `completeness` and `meterProgress` can compute a
 * "what if N more cards were filled" projection without double-rounding. */
function rawPct(filledCount: number, wizardComplete: boolean, total: number): number {
  const wizardPct = wizardComplete ? WIZARD_WEIGHT_PCT : 0;
  const cardsPct = (filledCount / total) * CARDS_WEIGHT_PCT;
  return Math.min(100, wizardPct + cardsPct);
}

/** Percentage of the whole bot configuration (wizard minimum + enrichment
 * cards) the owner has completed. `filled`/`total` stay card-only (they feed
 * the meter's `aria-valuenow`/`aria-valuemax`); `pct` reflects the full
 * 50/50 formula. */
export function completeness(profile: KbProfile): CompletenessResult {
  const total = ENRICHMENT_CARDS.length; // 9 (F167 added 'restrictions')
  const filled = ENRICHMENT_CARDS.filter((id) => cardFilled(profile, id)).length;
  const wizardComplete = wizardMinimumComplete(profile);
  const pct = Math.round(rawPct(filled, wizardComplete, total));
  return { filled, total, pct };
}

export interface MeterProgress {
  pct: number;
  /** Up to 2 unfilled cards, highest-impact first (IMPACT_ORDER order) — the
   * "add X and Y" half of the meter copy. */
  next: EnrichmentCardId[];
  /** Projected `pct` if every card in `next` were also filled. Equals `pct`
   * when `next` is empty (nothing left to recommend). */
  nextPct: number;
}

/** F174 FR2 — inputs for the meter copy: "Bot {{pct}}% configurado —
 * adicione {{next1}} e {{next2}} para chegar a {{nextPct}}%". `next` always
 * uses plain `IMPACT_ORDER` order (NOT `deriveRecommendations`, which layers
 * business-context priority rules on top — the meter promise is simply "the
 * next highest-impact things"). */
export function meterProgress(profile: KbProfile): MeterProgress {
  const total = ENRICHMENT_CARDS.length;
  const filled = ENRICHMENT_CARDS.filter((id) => cardFilled(profile, id)).length;
  const wizardComplete = wizardMinimumComplete(profile);
  const pct = Math.round(rawPct(filled, wizardComplete, total));
  const next = IMPACT_ORDER.filter((id) => !cardFilled(profile, id)).slice(0, 2);
  const nextPct = Math.round(rawPct(filled + next.length, wizardComplete, total));
  return { pct, next, nextPct };
}

// ── Recommendation logic ──────────────────────────────────────────────────────

/** F174 FR3 — a small regulated-vertical lexicon (health/law/finance-adjacent
 * pt-BR/pt-PT terms). Replaces the old `bot_goal === 'answer_only'` proxy,
 * which had no actual regulated signal (an `answer_only` bakery is not
 * regulated; a `capture_contacts` clinic is). */
const REGULATED_LEXICON = /saúde|clínica|médic|advog|jurídic|contab|financ|psic|terap|nutri/i;

/**
 * Returns up to 2 unfilled card ids that should carry the "Recomendado" badge.
 *
 * Priority rules (first match wins), then pad from IMPACT_ORDER:
 *   a) product / digital or send_to_link (link-first family) → 'links' (if unfilled)
 *   b) regulated essence (F174 FR3 — forbidden_scope AND restrictions both
 *      empty AND the essence text hits `REGULATED_LEXICON`) → 'limits' (if unfilled)
 *   c) no FAQ yet → 'faq'
 *   d) home_visit (domicilio) or shipping (physical_shipping) modality + no
 *      restrictions yet → 'restrictions' (F167 FR5 — coverage-area/delivery-zone
 *      businesses collect the most "we don't do X" edge cases)
 *   e) highest-impact unfilled card from IMPACT_ORDER
 *
 * Never includes an already-filled card. Returns [] if all cards are filled.
 */
export function deriveRecommendations(profile: KbProfile): EnrichmentCardId[] {
  const unfilled = new Set(ENRICHMENT_CARDS.filter((id) => !cardFilled(profile, id)));
  if (unfilled.size === 0) return [];

  const recommendations: EnrichmentCardId[] = [];

  // Rule (a): product/digital or the link-first family (F077) → links first
  const hasProductOffer =
    profile.offers.includes('products_physical') || profile.offers.includes('products_digital');
  const hasSalesGoal = profile.bot_goal === 'send_to_link';
  if ((hasProductOffer || hasSalesGoal) && unfilled.has('links')) {
    recommendations.push('links');
    unfilled.delete('links');
  }

  // Rule (b) (F174 FR3): forbidden_scope AND restrictions both empty + a
  // regulated-lexicon hit in the essence text (one_liner/ideal_customer) →
  // limits second. No bot_goal proxy anymore. `Array.isArray` guards mirror
  // `cardFilled` — a stale/partial profile response must read as "empty",
  // not throw (caught via the mobile-overflow-sweep against a pre-F167
  // fixture missing `restrictions` entirely).
  const isRegulatedEssence =
    (!Array.isArray(profile.forbidden_scope) || profile.forbidden_scope.length === 0) &&
    (!Array.isArray(profile.restrictions) || profile.restrictions.length === 0) &&
    REGULATED_LEXICON.test(`${profile.one_liner ?? ''} ${profile.ideal_customer ?? ''}`);
  if (recommendations.length < 2 && isRegulatedEssence && unfilled.has('limits')) {
    recommendations.push('limits');
    unfilled.delete('limits');
  }

  // Rule (c): no FAQ → faq
  if (recommendations.length < 2 && unfilled.has('faq')) {
    recommendations.push('faq');
    unfilled.delete('faq');
  }

  // Rule (d) (F167 FR5): a home-visit (domicilio) or shipping
  // (physical_shipping) modality is where coverage/delivery-zone edge cases
  // pile up — nudge toward restrictions when it's still empty.
  const hasCoverageOrShippingModality =
    profile.modalities.includes('domicilio') || profile.modalities.includes('physical_shipping');
  if (recommendations.length < 2 && hasCoverageOrShippingModality && unfilled.has('restrictions')) {
    recommendations.push('restrictions');
    unfilled.delete('restrictions');
  }

  // Pad with highest-impact unfilled cards from IMPACT_ORDER
  for (const id of IMPACT_ORDER) {
    if (recommendations.length >= 2) break;
    if (unfilled.has(id)) {
      recommendations.push(id);
      unfilled.delete(id);
    }
  }

  return recommendations;
}

// ── Card ordering ─────────────────────────────────────────────────────────────

/**
 * Returns all 8 card ids in display order:
 *   1. Recommended cards (from deriveRecommendations)
 *   2. Remaining unfilled cards in IMPACT_ORDER
 *   3. Filled cards (also in IMPACT_ORDER)
 */
export function orderCards(profile: KbProfile): EnrichmentCardId[] {
  const recommended = deriveRecommendations(profile);
  const recommendedSet = new Set(recommended);

  const unfilledNonRec = IMPACT_ORDER.filter(
    (id) => !cardFilled(profile, id) && !recommendedSet.has(id)
  );
  const filledByImpact = IMPACT_ORDER.filter((id) => cardFilled(profile, id));

  return [...recommended, ...unfilledNonRec, ...filledByImpact];
}
